import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { apiService } from '../services/api'
import { CheckCircle, XCircle, AlertCircle, User, Calendar, Phone, Shield, X } from 'lucide-react'
import { Helmet } from 'react-helmet-async'

interface PilotInfo {
  name: string
  pilotNumber: string
  competitionYear: string
  headshotUrl?: string
  emergencyContact: {
    name: string
    phone: string
    relationship: string
  }
}

interface VerificationResult {
  isValid: boolean
  competitionActive: boolean
  pilot?: PilotInfo
  message?: string
}

export default function VerifyPage() {
  const { userId } = useParams<{ userId: string }>()
  const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showPhotoModal, setShowPhotoModal] = useState(false)

  useEffect(() => {
    if (!userId) {
      setError('No pilot ID provided')
      setIsLoading(false)
      return
    }

    verifyPilot(userId)
  }, [userId])

  const verifyPilot = async (pilotId: string) => {
    setIsLoading(true)
    setError(null)

    try {
      // Public endpoint - no authentication required
      const response = await fetch(
        `${apiService.getBaseUrl()}/verify/${pilotId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to verify pilot')
      }

      const data = await response.json()
      console.log('[VerifyPage] Verification response:', data)
      
      // Handle wrapped response format
      const result = data.data || data
      setVerificationResult(result)
    } catch (err) {
      console.error('[VerifyPage] Verification error:', err)
      setError('Unable to verify pilot information')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Verify Pilot - FWO Pilot Portal</title>
        <meta name="description" content="Verify FlyWideOpen pilot membership card" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img 
                src="/fwo-logo.png" 
                alt="FlyWideOpen" 
                className="h-16" 
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Pilot Verification
            </h1>
            <p className="text-gray-600">
              FlyWideOpen Competition Portal
            </p>
          </div>

          {/* Content Card */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {isLoading ? (
              /* Loading State */
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-fwo-blue mb-4"></div>
                <p className="text-gray-600">Verifying pilot information...</p>
              </div>
            ) : error ? (
              /* Error State */
              <div className="p-12 text-center">
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Verification Failed
                </h2>
                <p className="text-gray-600">{error}</p>
              </div>
            ) : !verificationResult ? (
              /* No Result State */
              <div className="p-12 text-center">
                <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  No Information Available
                </h2>
                <p className="text-gray-600">
                  Unable to retrieve pilot information
                </p>
              </div>
            ) : !verificationResult.isValid ? (
              /* Invalid Pilot */
              <div className="p-12 text-center">
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Pilot Not Found
                </h2>
                <p className="text-gray-600">
                  {verificationResult.message || 'This pilot ID is not valid'}
                </p>
              </div>
            ) : !verificationResult.competitionActive ? (
              /* Competition Inactive */
              <div className="p-12 text-center">
                <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Competition Not Active
                </h2>
                <p className="text-gray-600 mb-6">
                  {verificationResult.message || 'The competition is not currently active'}
                </p>
                {verificationResult.pilot && (
                  <div className="text-sm text-gray-500">
                    <p>Pilot: {verificationResult.pilot.name}</p>
                    <p>Year: {verificationResult.pilot.competitionYear}</p>
                  </div>
                )}
              </div>
            ) : verificationResult.pilot ? (
              /* Valid Pilot - Show Information */
              <>
                {/* Success Header */}
                <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-6 text-white">
                  <div className="flex items-center justify-center space-x-3">
                    <CheckCircle className="h-12 w-12" />
                    <div>
                      <h2 className="text-2xl font-bold">Verified Pilot</h2>
                      <p className="text-green-100">Valid FlyWideOpen membership</p>
                    </div>
                  </div>
                </div>

                {/* Pilot Information */}
                <div className="p-8">
                  <div className="space-y-6">
                    {/* Pilot Name with Headshot */}
                    <div className="flex items-start space-x-4 pb-6 border-b border-gray-200">
                      {verificationResult.pilot.headshotUrl ? (
                        <button
                          type="button"
                          onClick={() => setShowPhotoModal(true)}
                          className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:ring-offset-2 rounded-full"
                          aria-label={`View larger photo of ${verificationResult.pilot.name}`}
                        >
                          <img
                            src={verificationResult.pilot.headshotUrl}
                            alt={verificationResult.pilot.name}
                            className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 cursor-pointer hover:border-fwo-blue hover:shadow-lg transition-all"
                            onError={(e) => {
                              const target = e.currentTarget
                              target.style.display = 'none'
                              const placeholder = target.closest('button')?.nextElementSibling as HTMLElement
                              if (placeholder) placeholder.style.display = 'flex'
                            }}
                          />
                        </button>
                      ) : null}
                      <div
                        className={`bg-blue-100 p-3 rounded-full flex-shrink-0 items-center justify-center ${verificationResult.pilot.headshotUrl ? 'hidden' : 'flex'}`}
                      >
                        <User className="h-6 w-6 text-fwo-blue" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-500 mb-1">Pilot Name</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {verificationResult.pilot.name}
                        </p>
                      </div>
                    </div>

                    {/* Competition Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-start space-x-3">
                        <Calendar className="h-5 w-5 text-gray-400 mt-1" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Competition Year</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {verificationResult.pilot.competitionYear}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <Shield className="h-5 w-5 text-gray-400 mt-1" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Pilot Number</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {verificationResult.pilot.pilotNumber}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Emergency Contact */}
                    <div className="pt-6 border-t border-gray-200">
                      <div className="flex items-start space-x-3">
                        <Phone className="h-5 w-5 text-gray-400 mt-1" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-500 mb-2">
                            Emergency Contact
                          </p>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="font-semibold text-gray-900">
                              {verificationResult.pilot.emergencyContact.name}
                            </p>
                            <p className="text-fwo-blue font-medium mt-2">
                              {verificationResult.pilot.emergencyContact.phone}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Verification Notice */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 text-center">
                        This pilot is verified for the {verificationResult.pilot.competitionYear} FlyWideOpen competition.
                        <br />
                        Emergency contact information is provided for safety purposes.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-sm text-gray-600">
              © {new Date().getFullYear()} FlyWideOpen. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Photo Enlargement Modal */}
      {showPhotoModal && verificationResult?.pilot?.headshotUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowPhotoModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`Enlarged photo of ${verificationResult.pilot.name}`}
        >
          <div className="relative max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowPhotoModal(false)}
              className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-fwo-blue z-10"
              aria-label="Close photo"
            >
              <X className="h-6 w-6 text-gray-700" />
            </button>
            <img
              src={verificationResult.pilot.headshotUrl}
              alt={verificationResult.pilot.name}
              className="w-full rounded-lg shadow-2xl object-cover"
            />
            <p className="text-center text-white mt-3 font-medium">
              {verificationResult.pilot.name}
            </p>
          </div>
        </div>
      )}
    </>
  )
}
